import React, { useState } from 'react';
import { ArrowLeft, Save, Eye, Share2, AlertCircle, RotateCcw, RotateCw, Trash2 } from 'lucide-react';
import { validateForm } from '../../../utils/formValidation'; // Import your validation
import DeleteAllConfirmModal from './DeleteAllConfirmModal';

const FormBuilderHeader = ({
  formTitle,
  onTitleChange,
  isSaving,
  isPublishing,
  lastSaved,
  onPreview,
  onPublish,
  onBack,
  formData, // Add formData prop to access full form
  hasUnsavedChanges, // Track if there are unpublished changes
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onDeleteAll,
  questionCount
}) => {
  const [validationErrors, setValidationErrors] = useState([]);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);

  const handlePublishClick = () => {
    // Validate form before publishing
    const validation = validateForm(formData);

    if (!validation.isValid) {
      // Show validation errors
      setValidationErrors(validation.errors);

      // Auto-hide errors after 5 seconds
      setTimeout(() => setValidationErrors([]), 5000);
      return;
    }

    // If valid, call parent publish handler
    if (onPublish) {
      onPublish();
    }
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 px-3 sm:px-4 md:px-6 py-2.5 sm:py-3">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Left Section */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <button
              className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              onClick={onBack}
            >
              <ArrowLeft size={18} className="sm:w-5 sm:h-5 text-gray-600" />
            </button>

            <input
              type="text"
              value={formTitle}
              onChange={onTitleChange}
              className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 bg-transparent border-none outline-none focus:bg-white focus:px-2 sm:focus:px-3 focus:py-1 focus:rounded-lg transition-all flex-1 min-w-0"
              placeholder="Untitled Form"
              data-tour="form-title"
            />
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-1 sm:gap-2 md:gap-3">
            {/* Undo/Redo Buttons */}
            <div className="hidden sm:flex items-center gap-1 border-r border-gray-200 pr-2 md:pr-3" data-tour="undo-redo">
              <button
                onClick={onUndo}
                disabled={!canUndo}
                title="Undo (Ctrl+Z)"
                className={`p-1.5 md:p-2 rounded-lg transition-colors ${
                  canUndo
                    ? 'hover:bg-gray-100 text-gray-700'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                <RotateCcw size={16} className="md:w-[18px] md:h-[18px]" />
              </button>
              <button
                onClick={onRedo}
                disabled={!canRedo}
                title="Redo (Ctrl+Y)"
                className={`p-1.5 md:p-2 rounded-lg transition-colors ${
                  canRedo
                    ? 'hover:bg-gray-100 text-gray-700'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                <RotateCw size={16} className="md:w-[18px] md:h-[18px]" />
              </button>
            </div>

            {/* Delete All Button */}
            {questionCount > 0 && (
              <button
                onClick={() => setShowDeleteAllModal(true)}
                title="Delete All Questions"
                className="hidden sm:block p-1.5 md:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-tour="delete-all"
              >
                <Trash2 size={16} className="md:w-[18px] md:h-[18px]" />
              </button>
            )}

            {/* Save Status */}
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500">
              {isSaving ? (
                <>
                  <Save size={16} className="animate-pulse" />
                  <span>Saving...</span>
                </>
              ) : lastSaved ? (
                <>
                  <Save size={16} className="text-green-600" />
                  <span>Saved</span>
                </>
              ) : null}
            </div>

            {/* Action Buttons */}
            <button
              onClick={onPreview}
              title='Preview'
              data-tour="preview"
              className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium"
            >
              <Eye size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span className="hidden sm:inline">Preview</span>
            </button>

            <button
              onClick={handlePublishClick}
              disabled={isPublishing}
              title={isPublishing ? 'Publishing...' : formData?.status === 'published' && !hasUnsavedChanges ? 'Published' : 'Publish'}
              data-tour="publish"
              className={`px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium ${
                isPublishing
                  ? 'bg-blue-400 text-white cursor-not-allowed'
                  : formData?.status === 'published' && !hasUnsavedChanges
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isPublishing ? (
                <>
                  <Share2 size={16} className="sm:w-[18px] sm:h-[18px] animate-pulse" />
                  <span className="hidden sm:inline">Publishing...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : formData?.status === 'published' && !hasUnsavedChanges ? (
                <>
                  <Share2 size={16} className="sm:w-[18px] sm:h-[18px] fill-current" />
                  <span className="hidden sm:inline">Published</span>
                  <span className="sm:hidden">✓</span>
                </>
              ) : (
                <>
                  <Share2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                  <span className="hidden sm:inline">Publish</span>
                  <span className="sm:hidden">⬆</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg animate-in slide-in-from-top duration-200">
            <div className="flex gap-3">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-900 mb-1">
                  Cannot publish form
                </h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Delete All Confirmation Modal */}
      <DeleteAllConfirmModal
        isOpen={showDeleteAllModal}
        onClose={() => setShowDeleteAllModal(false)}
        onConfirm={onDeleteAll}
        questionCount={questionCount}
      />
    </>
  );
};

export default FormBuilderHeader;